import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Clock, Copy, Link2, MoreHorizontal, X } from 'lucide-react';
import { Badge, SeverityBadge, StatusBadge, Tabs, TabList, Tab, TabPanel } from '@/components/primitives';
import WorldMap from '@/components/map/WorldMap';
import BriefDoc from '@/pages/inbox/BriefDoc';
import useResource from '@/pages/dashboard/useResource';
import { apiGet } from '@/pages/dashboard/api';
import { briefId, cleanText, copyText, shortId } from '@/pages/inbox/inboxUtils';
import { slaWindowLabel } from '@/lib/sla';
import ActivityTab from './ActivityTab';
import { formatUtcShort, ownerView } from './threatUtils';

const TYPE = { attack: 'Attack', advisory: 'Advisory', informational: 'Informational' };

function MoreMenu({ actions }) {
  const items = [...actions.triage.filter((a) => a.id !== 'ack'), ...actions.exports];
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button type="button" className="is-btn is-btn--secondary is-btn--sm" aria-label="More actions"><MoreHorizontal aria-hidden="true" /></button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={6} className="sh-pop" style={{ minWidth: 260 }}>
          {items.map((a, i) => {
            const Icon = a.icon;
            const sep = i > 0 && items[i - 1].id !== 'reset' && a.id === 'sigma';
            return (
              <React.Fragment key={a.id}>
                {sep && <Dropdown.Separator className="sh-pop-sep" />}
                <Dropdown.Item
                  className="sh-pop-item"
                  disabled={a.disabled}
                  onSelect={() => (a.disabled ? a.reason && toast.info(a.reason) : a.run())}
                  title={a.disabled ? a.reason || undefined : undefined}
                >
                  {Icon && <Icon className="sh-ic" aria-hidden="true" />}
                  <span>{a.label}{a.count != null ? ` (${a.count})` : ''}</span>
                  {a.keys && <kbd className="th-kbd">{a.keys}</kbd>}
                </Dropdown.Item>
              </React.Fragment>
            );
          })}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

/** Where the threat is aimed, from its analysis. Countries are drawn; a region or "worldwide" is said in words. */
function Places({ attackId }) {
  const places = useResource(() => apiGet(`/geo/threat/${encodeURIComponent(attackId)}`), [attackId], { poll: false });
  const p = places.data;
  if (!p || (!p.countries.length && !p.regions.length && !p.worldwide)) return null;
  const area = p.countries.length ? p.countries.map((c) => c.iso2) : p.region_countries;
  const text = p.countries.length ? p.countries.map((c) => c.name).join(', ') : p.regions.length ? p.regions.join(', ') : 'Worldwide, no single region';
  return (
    <section className="th-places" aria-label="Where it is aimed">
      <div className="th-places-text">
        <span className="th-places-l">Aimed at</span>
        <span>{text}</span>
        {p.in_footprint.length > 0 && <span className="th-places-fp">Includes your footprint</span>}
      </div>
      {area.length > 0 && (
        <Link to={p.countries.length === 1 ? `/map?layer=threats&country=${p.countries[0].iso2}` : '/map?layer=threats'} className="th-places-map" aria-label="Open on the threat map">
          <WorldMap values={Object.fromEntries(area.map((a) => [a, 1]))} fit={area} compact height={120} label={`Map: ${text}`} />
        </Link>
      )}
    </section>
  );
}

/**
 * The selected threat: who owns it, when it is due, the decision buttons, and
 * the brief itself with its activity. Desktop: a panel beside the queue;
 * tablets: a drawer over it; phones: a full screen with a way back.
 */
const DetailPanel = forwardRef(function DetailPanel({
  brief, status, statusItem, owner, me, sla, rules, actions, onClose, onAssign, mode,
}, headingRef) {
  const id = briefId(brief);
  const [tab, setTab] = useState('brief');
  const [count, setCount] = useState(null);
  const [activityKey, setActivityKey] = useState(0);
  const who = ownerView(owner, me);
  useEffect(() => { setTab('brief'); setCount(null); }, [id]);
  // A status or owner change is new activity: refresh the tab if it is showing.
  useEffect(() => { setActivityKey((k) => k + 1); }, [status, owner?.owner_id]);
  const onCount = useCallback((n) => setCount(n), []);

  const copyLink = async () => {
    const ok = await copyText(`${window.location.origin}/threats?id=${encodeURIComponent(id)}`).then(() => true, () => false);
    if (ok) toast.success('Link copied'); else toast.error("Couldn't copy to the clipboard");
  };
  const copyId = async () => {
    const ok = await copyText(id).then(() => true, () => false);
    if (ok) toast.success('Threat ID copied'); else toast.error("Couldn't copy to the clipboard");
  };
  const ack = actions.byId.ack;
  const reopen = actions.byId.reset;

  return (
    <aside className={`th-panel th-panel--${mode}`} aria-label="Selected threat" data-testid="threat-panel">
      <div className="th-panel-head">
        <div className="th-panel-top">
          <SeverityBadge severity={brief.severity} />
          <Badge size="sm">{TYPE[brief.content_type || 'attack']}</Badge>
          <button type="button" className="th-id" onClick={copyId} title="Copy the full threat ID">
            <span className="mono">{shortId(id)}</span><Copy aria-hidden="true" />
          </button>
          <span className="th-spacer" />
          <button type="button" className="sh-icon-btn" onClick={copyLink} aria-label="Copy link to this threat" title="Copy link"><Link2 className="sh-ic" aria-hidden="true" /></button>
          <button type="button" className="sh-icon-btn" onClick={onClose} aria-label={mode === 'screen' ? 'Back to the queue' : 'Close the panel'} title="Close (Esc)"><X className="sh-ic" aria-hidden="true" /></button>
        </div>
        <h2 className="th-panel-title" ref={headingRef} tabIndex={-1}>{cleanText(brief.name)}</h2>
        {sla && (
          <div className={`th-sla-line th-sla-line--${sla.state}`}>
            <Clock aria-hidden="true" />
            <span>
              {sla.state === 'overdue' || sla.state === 'soon' || sla.state === 'ok' ? `${sla.label}. ` : `Target ${sla.label.toLowerCase()}. `}
              {sla.severity} threats are due within {slaWindowLabel(sla.hours)} of matching ({formatUtcShort(sla.due)}).
            </span>
          </div>
        )}
        <div className="th-panel-actions">
          {status === 'acked' || status === 'shipped' || status === 'archived' ? (
            reopen && <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={reopen.run}>Reopen</button>
          ) : (
            ack && <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={ack.run} disabled={ack.disabled}>Acknowledge<kbd className="th-kbd th-kbd--on">A</kbd></button>
          )}
          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => onAssign(brief, who ? null : 'me')}>
            {who ? 'Unassign' : 'Assign to me'}{!who && <kbd className="th-kbd">I</kbd>}
          </button>
          <MoreMenu actions={actions} />
        </div>
        <dl className="th-facts">
          <div><dt>Status</dt><dd><StatusBadge status={status} size="sm" /></dd></div>
          <div><dt>Owner</dt><dd>{who ? who.label : <span className="th-muted">Unassigned</span>}</dd></div>
          <div><dt>Detected</dt><dd>{formatUtcShort(brief.discovered_at)}</dd></div>
          <div><dt>Sources</dt><dd>{brief.source_count || 1}</dd></div>
        </dl>
        <Places attackId={id} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabList label="Threat sections" className="th-tabs">
          <Tab value="brief">Brief</Tab>
          <Tab value="activity" count={count ?? undefined}>Activity</Tab>
        </TabList>
        <TabPanel value="brief">
          <BriefDoc brief={brief} rules={rules} />
        </TabPanel>
        <TabPanel value="activity">
          <ActivityTab attackId={id} onCount={onCount} refreshKey={activityKey} />
        </TabPanel>
      </Tabs>
    </aside>
  );
});

export default DetailPanel;
